<template>
  <!-- Teleported like the PrimeVue Popover it replaces (append-to="body"): a transformed ancestor
       (e.g. the bank-pull chip's attention pulse) would otherwise scale the panel and become the
       containing block for its fixed positioning. -->
  <Teleport to="body">
    <wa-popup
      ref="popup"
      class="ui-popover"
      :anchor.prop="anchor"
      :active="open || undefined"
      :placement="alignEnd ? 'bottom-end' : 'bottom-start'"
      strategy="fixed"
      flip
      shift
      :shift-padding="8"
      v-bind="$attrs"
    >
      <!-- Content exists only while open, like PrimeVue's Popover. -->
      <slot v-if="open" />
    </wa-popup>
  </Teleport>
</template>

<script setup lang="ts">
/**
 * Anchored floating panel on Web Awesome's `<wa-popup>` — replaces the Volt `VPopover`
 * (PrimeVue `Popover`). Same imperative API (`show(event)`, `hide()`, `toggle(event)`,
 * `contains(target)`) so the trigger keeps its own open/close rules (hover vs tap).
 *
 * Why `wa-popup` and not `wa-popover`: `wa-popover` opens itself on any click of its anchor, which
 * the bank-pull chip must not do on desktop (it opens on hover there). `wa-popup` only positions.
 *
 * Positioning parity with PrimeVue: below the trigger, left edges aligned (`alignEnd` = right edges
 * aligned), flipped above when there's no room below, no gap. Escape closes it, as PrimeVue's did.
 * The panel look lives in webawesome-theme.css (`wa-popup.ui-popover::part(popup)`).
 */
import '@awesome.me/webawesome/dist/components/popup/popup.js'

import { onBeforeUnmount, ref, watch } from 'vue'

defineOptions({ inheritAttrs: false })

withDefaults(defineProps<{
  /** Right-align the panel to the trigger, so it hangs to the left (trigger at the right end of a bar). */
  alignEnd?: boolean
}>(), { alignEnd: false })

const emit = defineEmits<{ show: []; hide: [] }>()

const popup = ref<HTMLElement>()
const open = ref(false)
const anchor = ref<Element | null>(null)

function show(event?: Event, target?: Element) {
  const next = target ?? (event?.currentTarget as Element | null) ?? anchor.value
  if (next) anchor.value = next
  if (!anchor.value) return
  if (!open.value) emit('show')
  open.value = true
}

function hide() {
  if (open.value) emit('hide')
  open.value = false
}

function toggle(event?: Event, target?: Element) {
  if (open.value) hide()
  else show(event, target)
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') hide()
}

watch(open, (isOpen) => {
  if (isOpen) document.addEventListener('keydown', onKeydown)
  else document.removeEventListener('keydown', onKeydown)
})
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown))

defineExpose({
  show,
  hide,
  toggle,
  /** True while the panel is open. */
  isOpen: () => open.value,
  // The panel is teleported out of the trigger's subtree, so a plain el.contains() outside-click
  // check can't see it. Pointer events retargeted out of the popup's shadow root land on the host.
  contains: (target: EventTarget | null) => !!(popup.value && target instanceof Node && popup.value.contains(target)),
})
</script>
